import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useFasAttendants, useLinkAttendant, useMemberAttendance } from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';
import { Avatar, CardSkeleton, ErrorState, SectionHead } from '@/components/ui';
import { GrowthTag } from '@/components/GrowthTag';
import { CountChart, type CountSeries } from '@/components/attendance/CountChart';
import { IconChevronRight, IconClock } from '@/components/Icons';
import {
  APPOINTMENT_LABELS, ASSIGNMENT_METHOD_LABELS, CHANNEL_LABELS, CONVERSATION_STATUS_LABELS, FAS_ROLE_LABELS, MESES,
  PERIOD_LABELS, ROLE_LABELS, STATUS_LABELS, formatBRL, formatBRLShort, formatDate, formatDuration, formatISODateTime,
  formatNumber, formatRate, todayISO,
} from '@/lib/format';
import type { AttendanceReport, MemberAttendance, PeriodKey, Rank } from '@/lib/types';

const PERIODS: PeriodKey[] = ['month', 'lastMonth', 'week', 'quarter', 'year', 'all'];
const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const plural = (count: number, one: string, many: string) => `${formatNumber(count)} ${count === 1 ? one : many}`;
const rankText = (rank: Rank | null) => (rank ? `${rank.position}º de ${rank.of} na equipe` : null);
const growthOf = (current: number, previous: number) => (previous > 0 ? (current - previous) / previous : null);

/**
 * Tudo do atendimento de um membro, lido da For All Sales: leads, tempo de
 * resposta, conversão, negócios, fila e os leads mais recentes — sempre ao
 * lado da média da equipe. Só administradores chegam aqui (Membros → linha).
 */
export function MemberDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [period, setPeriod] = useState<PeriodKey>('month');
  const [from, setFrom] = useState(() => `${todayISO().slice(0, 8)}01`);
  const [to, setTo] = useState(todayISO);
  const custom = period === 'custom';

  const { data, isLoading, isPlaceholderData, error } = useMemberAttendance(
    id,
    custom ? { from: from || undefined, to: to || undefined } : { period },
  );

  if (error) return <ErrorState message={(error as Error).message} />;

  return (
    <>
      <nav className="muted" style={{ fontSize: 12.5, marginBottom: 10 }} aria-label="Você está em">
        <Link to="/membros" className="link-btn">Membros</Link>
        <span aria-hidden> / </span>
        <span>{data?.member.name ?? '…'}</span>
      </nav>

      {data ? <MemberHeader data={data} /> : <CardSkeleton height={64} />}

      <div className="toolbar" style={{ marginTop: 18 }}>
        <select className="inp" value={period} onChange={(event) => setPeriod(event.target.value as PeriodKey)} aria-label="Período">
          {PERIODS.map((option) => <option key={option} value={option}>{PERIOD_LABELS[option]}</option>)}
          <option value="custom">Personalizado</option>
        </select>
        {custom ? (
          <>
            <input className="inp" style={{ width: 150 }} type="date" value={from} onChange={(event) => setFrom(event.target.value)} aria-label="Data inicial" />
            <span className="muted" style={{ fontSize: 12 }}>até</span>
            <input className="inp" style={{ width: 150 }} type="date" value={to} onChange={(event) => setTo(event.target.value)} aria-label="Data final" />
          </>
        ) : null}
        <div className="spacer" />
        {data?.integration.lastSuccessAt ? (
          <span className="muted" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <IconClock /> For All Sales lida em {formatISODateTime(data.integration.lastSuccessAt)}
          </span>
        ) : null}
      </div>

      {isLoading || !data ? (
        <div className="grid grid2"><CardSkeleton height={220} /><CardSkeleton height={220} /></div>
      ) : !data.report || !data.attendant ? (
        <NotLinked data={data} />
      ) : (
        <div className={`metrics-body${isPlaceholderData ? ' is-stale' : ''}`} aria-busy={isPlaceholderData}>
          <ReportBody data={data} report={data.report} />
        </div>
      )}
    </>
  );
}

function MemberHeader({ data }: { data: MemberAttendance }) {
  const { member, attendant } = data;
  return (
    <header style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14 }}>
      <Avatar name={member.name} color={member.avatarColor} photo={member.photoUrl} size={52} />
      <div style={{ minWidth: 0, flex: '1 1 260px' }}>
        <h1 className="page-title" style={{ margin: 0 }}>{member.name}</h1>
        <div className="muted" style={{ fontSize: 13, marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: '4px 10px', alignItems: 'center' }}>
          <span>{member.login}{member.email ? ` · ${member.email}` : ''}</span>
          <span className="chip">{ROLE_LABELS[member.role]}</span>
          {!member.active ? <span className="chip chip-negative">inativo</span> : null}
          {attendant ? (
            <span className="chip chip-primary" style={{ whiteSpace: 'normal', maxWidth: '100%', height: 'auto' }} title="Atendente vinculado na For All Sales">
              For All Sales: {attendant.name}
              {attendant.attendanceRole ? ` · ${FAS_ROLE_LABELS[attendant.attendanceRole] ?? attendant.attendanceRole}` : ''}
              {attendant.squads ? ` · ${attendant.squads}` : ''}
            </span>
          ) : null}
          {attendant && !attendant.active ? <span className="chip chip-warning">inativo na For All Sales</span> : null}
        </div>
      </div>
    </header>
  );
}

/** Membro sem atendente vinculado: explica e deixa vincular ali mesmo. */
function NotLinked({ data }: { data: MemberAttendance }) {
  const toast = useToast();
  const { data: attendants, isLoading } = useFasAttendants({ enabled: data.integration.enabled });
  const link = useLinkAttendant();
  const free = (attendants ?? []).filter((attendant) => !attendant.member);
  const suggested = free.find((attendant) => attendant.email && attendant.email === data.member.email?.toLowerCase());
  const [choice, setChoice] = useState('');
  const selected = choice || (suggested ? String(suggested.id) : '');

  if (!data.integration.enabled) {
    return (
      <div className="alert alert-info" style={{ marginTop: 8 }}>
        <div>
        A leitura da For All Sales ainda não está ligada no servidor (falta <code>FAS_DATABASE_URL</code>). Assim que
        estiver, os números de atendimento de cada membro aparecem aqui.
        <SalesLine data={data} />
        </div>
      </div>
    );
  }

  return (
    <section className="card" style={{ marginTop: 8, maxWidth: 640 }}>
      <h2 className="figure-panel-title" style={{ fontSize: 15 }}>Sem vínculo com a For All Sales</h2>
      <p className="muted" style={{ fontSize: 13, margin: '8px 0 14px' }}>
        Vincule {data.member.name.split(' ')[0]} à pessoa que atende na conta {data.integration.accountName ?? 'da operação'} para
        ver leads, tempo de resposta e conversão — e para os leads dela virem para cá, em Clientes.
      </p>
      <div className="toolbar" style={{ margin: 0 }}>
        <select className="inp" value={selected} onChange={(event) => setChoice(event.target.value)} aria-label="Atendente da For All Sales" disabled={isLoading}>
          <option value="">{isLoading ? 'Carregando a equipe…' : 'Escolha o atendente'}</option>
          {free.map((attendant) => (
            <option key={attendant.id} value={attendant.id}>
              {attendant.name}{attendant.email ? ` · ${attendant.email}` : ''}{attendant.active ? '' : ' (inativo)'}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!selected || link.isPending}
          onClick={async () => {
            try {
              const result = await link.mutateAsync({ attendantId: Number(selected), userId: data.member.id });
              toast.success(result.owners > 0 ? `Vinculado. ${plural(result.owners, 'cliente passou', 'clientes passaram')} para ${data.member.name}.` : 'Vinculado.');
            } catch (caught) {
              toast.fromError(caught);
            }
          }}
        >
          Vincular
        </button>
      </div>
      <SalesLine data={data} />
    </section>
  );
}

function SalesLine({ data }: { data: MemberAttendance }) {
  return (
    <p className="muted" style={{ fontSize: 12.5, margin: '14px 0 0' }}>
      Na plataforma, em {data.range.label.toLowerCase()}: {plural(data.sales.receipts, 'comprovante', 'comprovantes')}, {formatBRL(data.sales.sold)} vendido.
    </p>
  );
}

/* ------------------------------------------------------------- relatório */

function ReportBody({ data, report }: { data: MemberAttendance; report: AttendanceReport }) {
  const { kpis, team, rank, previous, activity, conversion, deals } = report;
  const comparable = data.range.key !== 'all' && previous !== null;
  const firstName = data.member.name.split(' ')[0];

  if (kpis.leadsReceived === 0 && activity.messages === 0 && deals.won + deals.lost === 0) {
    return (
      <div className="empty" style={{ marginTop: 12 }}>
        {firstName} não recebeu leads nem enviou mensagens em <strong>{data.range.label.toLowerCase()}</strong> na For All Sales.
        <QueueNow report={report} compact />
      </div>
    );
  }

  return (
    <>
      <section>
        <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>Leads recebidos · {data.range.label}</div>
        <div className="metrics-hero">
          <span className="metrics-hero-value">{formatNumber(kpis.leadsReceived)}</span>
          {comparable ? <GrowthTag growth={growthOf(kpis.leadsReceived, previous!.leadsReceived)} previousLabel={formatNumber(previous!.leadsReceived)} /> : null}
        </div>
        <div className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>
          {plural(kpis.answered, 'atendido', 'atendidos')} · {plural(kpis.noReply, 'sem resposta', 'sem resposta')}
          {kpis.aiOnly > 0 ? ` · ${plural(kpis.aiOnly, 'só pela IA', 'só pela IA')}` : ''}
          {' · '}média da equipe {formatNumber(team.leadsReceived)} por pessoa
          {rank.leadsReceived ? ` · ${rankText(rank.leadsReceived)}` : ''}
        </div>
      </section>

      <div className="metric-row" style={{ marginTop: 26 }}>
        <Kpi
          label="1ª resposta (mediana)"
          value={formatDuration(kpis.firstResponseMedian)}
          sub={
            <>
              média {formatDuration(kpis.firstResponseAvg)} · 90% em até {formatDuration(kpis.firstResponseP90)}
              <br />equipe {formatDuration(team.firstResponseMedian)}{rank.firstResponseMedian ? ` · ${rankText(rank.firstResponseMedian)}` : ''}
            </>
          }
          before={comparable ? formatDuration(previous!.firstResponseMedian) : null}
        />
        <Kpi
          label="Tempo médio de resposta"
          value={formatDuration(kpis.replyAvg)}
          sub={<>em {plural(kpis.replies, 'resposta', 'respostas')} ao lead<br />equipe {formatDuration(team.replyAvg)}</>}
          before={comparable ? formatDuration(previous!.replyAvg) : null}
        />
        <Kpi
          label={`Respondidos em até ${report.slaMinutes} min`}
          value={formatRate(kpis.slaRate)}
          sub={<>{formatNumber(kpis.slaWithin)} de {formatNumber(kpis.slaBase)} primeiras respostas<br />equipe {formatRate(team.slaRate)}</>}
          before={comparable ? formatRate(previous!.slaRate) : null}
        />
        <Kpi
          label="Taxa de resposta"
          value={formatRate(kpis.answerRate)}
          sub={<>leads com resposta de {firstName}<br />equipe {formatRate(team.answerRate)}{rank.answerRate ? ` · ${rankText(rank.answerRate)}` : ''}</>}
          before={comparable ? formatRate(previous!.answerRate) : null}
        />
      </div>

      <div className="metric-row" style={{ borderTop: 'none' }}>
        <Kpi
          label="Mensagens enviadas"
          value={formatNumber(activity.messages)}
          sub={
            <>
              {plural(activity.activeDays, 'dia ativo', 'dias ativos')}
              {activity.messagesPerActiveDay !== null ? ` · ${formatNumber(activity.messagesPerActiveDay)} por dia` : ''}
              <br />equipe {formatNumber(team.messages)} por pessoa{rank.messages ? ` · ${rankText(rank.messages)}` : ''}
            </>
          }
        />
        <Kpi
          label="Conversão dos leads"
          value={formatRate(conversion.rate)}
          sub={
            <>
              {formatNumber(conversion.wonLeads)} de {formatNumber(conversion.leads)} viraram cliente
              {conversion.ephraPurchases > 0 ? <><br />{plural(conversion.ephraPurchases, 'compra', 'compras')} na Ephra · {formatBRLShort(conversion.ephraAmount)}</> : null}
            </>
          }
        />
        <Kpi
          label="Negócios ganhos"
          value={formatNumber(deals.won)}
          sub={
            <>
              {formatBRL(deals.wonValue)}{deals.winRate !== null ? ` · ${formatRate(deals.winRate)} de ganho` : ''}
              <br />equipe {formatNumber(team.won)} por pessoa{rank.won ? ` · ${rankText(rank.won)}` : ''}
            </>
          }
        />
        <Kpi
          label="Vendido na plataforma"
          value={formatBRLShort(data.sales.sold)}
          sub={<>{plural(data.sales.receipts, 'comprovante', 'comprovantes')} no período</>}
        />
      </div>

      <DailyChart report={report} />

      <div className="grid grid2 figure-grid" style={{ marginTop: 34 }}>
        <ResponseBuckets report={report} />
        <QueueNow report={report} />
      </div>

      <div className="grid grid2 figure-grid" style={{ marginTop: 22 }}>
        <Channels report={report} />
        <Outcomes report={report} />
      </div>

      <Arrivals report={report} />

      <RecentLeads report={report} />

      <p className="muted" style={{ fontSize: 12, marginTop: 18, lineHeight: 1.55 }}>
        Números da conta {data.integration.accountName ?? 'da operação'} na For All Sales. Leads, respostas e SLA contam os
        leads que entraram no período e estão com {firstName}; mensagens contam o que foi enviado no período. 1ª resposta
        é do primeiro contato do lead até a primeira mensagem de uma pessoa da equipe — IA, disparo e automação não contam.
        Os tempos são corridos (noites e fins de semana entram).
      </p>
    </>
  );
}

function Kpi({ label, value, sub, before }: { label: string; value: ReactNode; sub?: ReactNode; before?: string | null }) {
  return (
    <div className="metric">
      <span className="label">{label}</span>
      <span className="metric-value num">{value}</span>
      {sub ? <span className="metric-sub" style={{ lineHeight: 1.5 }}>{sub}</span> : null}
      {before ? <span className="metric-sub">período anterior: {before}</span> : null}
    </div>
  );
}

function pointTitle(key: string, granularity: 'day' | 'month'): string {
  if (granularity === 'day') return formatDate(key);
  const [year, month] = key.split('-');
  return `${MESES[Number(month) - 1] ?? ''} de ${year}`;
}

function DailyChart({ report }: { report: AttendanceReport }) {
  const { granularity, points } = report.series;
  const labels = useMemo(() => points.map((p) => (granularity === 'day' ? p.key.slice(8, 10) + '/' + p.key.slice(5, 7) : (MESES[Number(p.key.slice(5, 7)) - 1] ?? '').slice(0, 3))), [points, granularity]);
  const titles = useMemo(() => points.map((p) => pointTitle(p.key, granularity)), [points, granularity]);
  const series = useMemo<CountSeries[]>(
    () => [
      { key: 'received', label: 'Leads recebidos', token: '--series-1', mark: 'bar', values: points.map((p) => p.leadsReceived) },
      { key: 'answered', label: 'Atendidos', token: '--series-3', mark: 'bar', values: points.map((p) => p.answered) },
      { key: 'messages', label: 'Mensagens enviadas', token: '--series-2', mark: 'line', values: points.map((p) => p.messages), secondary: true },
    ],
    [points],
  );
  if (points.length === 0) return null;
  return (
    <section style={{ marginTop: 34 }}>
      <SectionHead title={granularity === 'day' ? 'Dia a dia' : 'Mês a mês'} />
      <CountChart labels={labels} titles={titles} series={series} ariaLabel="Leads recebidos, atendidos e mensagens enviadas ao longo do período" />
    </section>
  );
}

function BarLine({ label, count, max, tone, hint }: { label: string; count: number; max: number; tone: string; hint?: string }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(64px, 130px) minmax(0, 1fr) 44px', alignItems: 'center', gap: 10, fontSize: 12.5, padding: '5px 0' }}>
      <span className="muted" title={hint}>{label}</span>
      <div className="bar-track" style={{ height: 10 }}>
        <div className="bar-fill" style={{ width: `${max > 0 ? (count / max) * 100 : 0}%`, background: `var(${tone})` }} />
      </div>
      <span className="num right">{formatNumber(count)}</span>
    </div>
  );
}

function ResponseBuckets({ report }: { report: AttendanceReport }) {
  const max = Math.max(1, ...report.responseBuckets.map((b) => b.count));
  return (
    <section className="card figure-panel">
      <h2 className="figure-panel-title">Tempo até a 1ª resposta</h2>
      <div>
        {report.responseBuckets.map((bucket) => (
          <BarLine
            key={bucket.key}
            label={bucket.label}
            count={bucket.count}
            max={max}
            tone={bucket.key === 'none' ? '--muted-foreground' : bucket.key === 'lt5' || bucket.key === 'm5_15' ? '--series-3' : bucket.key === 'gt4' ? '--series-2' : '--series-1'}
          />
        ))}
      </div>
    </section>
  );
}

function QueueNow({ report, compact }: { report: AttendanceReport; compact?: boolean }) {
  const { queue } = report;
  const rows: Array<[string, ReactNode, string?]> = [
    ['Conversas abertas', formatNumber(queue.open)],
    ['Pendentes', formatNumber(queue.pending)],
    ['Esperando resposta', formatNumber(queue.awaitingReply), queue.awaitingReply > 0 ? 'warning' : undefined],
    ['Paradas há mais de 24 h', formatNumber(queue.stale24h), queue.stale24h > 0 ? 'warning' : undefined],
    ['Espera mais antiga desde', queue.oldestWaitingAt ? formatISODateTime(queue.oldestWaitingAt) : '—'],
  ];
  const list = (
    <dl className="figure-list">
      {rows.map(([label, value, tone]) => (
        <div key={label}><dt>{label}</dt><dd className={tone}>{value}</dd></div>
      ))}
    </dl>
  );
  if (compact) return <div style={{ maxWidth: 360, margin: '16px auto 0', textAlign: 'left' }}><div className="label" style={{ marginBottom: 6 }}>Fila agora</div>{list}</div>;
  return (
    <section className="card figure-panel">
      <h2 className="figure-panel-title">Fila agora <span className="muted" style={{ fontWeight: 400 }}>· independe do período</span></h2>
      {list}
    </section>
  );
}

function Channels({ report }: { report: AttendanceReport }) {
  return (
    <section className="card figure-panel" style={{ minWidth: 0 }}>
      <h2 className="figure-panel-title">Por canal</h2>
      {report.channels.length === 0 ? (
        <span className="muted" style={{ fontSize: 13 }}>Nenhum lead no período.</span>
      ) : (
        <div style={{ overflowX: 'auto', minWidth: 0 }}>
        <table className="mini-tbl">
          <thead><tr><th>Canal</th><th>Leads</th><th>Resposta</th><th>1ª resposta</th><th>Resolvidos</th></tr></thead>
          <tbody>
            {report.channels.map((row) => (
              <tr key={row.channel}>
                <th>{CHANNEL_LABELS[row.channel] ?? row.channel}</th>
                <td>{formatNumber(row.leads)}</td>
                <td>{formatRate(row.answerRate)}</td>
                <td>{formatDuration(row.firstResponseMedian)}</td>
                <td>{formatNumber(row.resolved)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </section>
  );
}

function Outcomes({ report }: { report: AttendanceReport }) {
  const { deals, appointments, transfers, kpis } = report;
  const meetings = Object.entries(appointments);
  return (
    <section className="card figure-panel">
      <h2 className="figure-panel-title">Negócios, reuniões e transferências</h2>
      <dl className="figure-list is-wrapping">
        <div><dt>Negócios ganhos</dt><dd>{formatNumber(deals.won)} · {formatBRL(deals.wonValue)}</dd></div>
        <div><dt>Negócios perdidos</dt><dd>{formatNumber(deals.lost)}</dd></div>
        <div><dt>Em aberto agora</dt><dd>{formatNumber(deals.open)} · {formatBRL(deals.openValue)}</dd></div>
        {deals.lostReasons.length > 0 ? (
          <div><dt>Motivos de perda</dt><dd style={{ textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' }}>{deals.lostReasons.map((r) => `${r.reason} (${r.count})`).join(', ')}</dd></div>
        ) : null}
        <div>
          <dt>Reuniões</dt>
          <dd>{meetings.length === 0 ? '—' : meetings.map(([status, count]) => `${formatNumber(count)} ${(APPOINTMENT_LABELS[status] ?? status).toLowerCase()}`).join(' · ')}</dd>
        </div>
        <div>
          <dt>Leads que chegaram</dt>
          <dd>{transfers.received === 0 ? '—' : transfers.byMethod.map((m) => `${formatNumber(m.count)} ${(ASSIGNMENT_METHOD_LABELS[m.method] ?? m.method).toLowerCase()}`).join(' · ')}</dd>
        </div>
        <div><dt>Repassados a outra pessoa</dt><dd>{formatNumber(transfers.passedOn)}</dd></div>
        <div><dt>Resolvidos</dt><dd>{formatNumber(kpis.resolved)} ({formatRate(kpis.resolutionRate)}) · em {formatDuration(kpis.resolutionAvg)}</dd></div>
      </dl>
    </section>
  );
}

function Arrivals({ report }: { report: AttendanceReport }) {
  const { hours, weekdays } = report.arrivals;
  const maxHour = Math.max(1, ...hours);
  const maxDay = Math.max(1, ...weekdays);
  if (hours.every((h) => h === 0)) return null;
  return (
    <section style={{ marginTop: 34 }}>
      <SectionHead title="Quando os leads chegam" />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-end' }}>
        <div style={{ flex: '3 1 320px', minWidth: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(24, minmax(0, 1fr))', gap: 3, alignItems: 'end', height: 90 }} role="img" aria-label="Leads por hora de chegada">
            {hours.map((count, hour) => (
              <div key={hour} title={`${hour}h: ${formatNumber(count)} ${count === 1 ? 'lead' : 'leads'}`} style={{ height: `${Math.max(2, (count / maxHour) * 100)}%`, background: count ? 'var(--series-1)' : 'var(--muted)', borderRadius: 2 }} />
            ))}
          </div>
          <div className="muted num" style={{ display: 'grid', gridTemplateColumns: 'repeat(24, minmax(0, 1fr))', fontSize: 10, marginTop: 4 }}>
            {hours.map((_, hour) => <span key={hour} style={{ textAlign: 'center' }}>{hour % 3 === 0 ? `${hour}h` : ''}</span>)}
          </div>
        </div>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          {weekdays.map((count, day) => <BarLine key={day} label={WEEKDAYS[day]!} count={count} max={maxDay} tone="--series-1" />)}
        </div>
      </div>
    </section>
  );
}

function RecentLeads({ report }: { report: AttendanceReport }) {
  if (report.recent.length === 0) return null;
  return (
    <section style={{ marginTop: 34 }}>
      <SectionHead title="Leads do período">
        <Link to="/clientes" className="link-btn muted" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          Ver em Clientes <IconChevronRight />
        </Link>
      </SectionHead>
      <div className="table-wrap">
        <table className="tbl tbl-cards">
          <thead>
            <tr>
              <th>Lead</th>
              <th>Canal</th>
              <th>Entrou</th>
              <th className="right">1ª resposta</th>
              <th className="right">Mensagens</th>
              <th>Conversa</th>
              <th>Cliente</th>
            </tr>
          </thead>
          <tbody>
            {report.recent.map((row) => (
              <tr key={row.id}>
                <td className="cell-main">
                  <div style={{ fontWeight: 500 }}>{row.contactName}</div>
                  {row.stageName ? <div className="muted" style={{ fontSize: 12 }}>{row.stageName}</div> : null}
                </td>
                <td data-label="Canal">{CHANNEL_LABELS[row.channel] ?? row.channel}</td>
                <td className="num nowrap" data-label="Entrou">{formatISODateTime(row.startedAt)}</td>
                <td className="num right cell-key" data-label="1ª resposta">{formatDuration(row.firstResponseSeconds)}</td>
                <td className="num right cell-third" data-label="Mensagens">{formatNumber(row.humanMessages)}</td>
                <td className="cell-third" data-label="Conversa">
                  <span className="chip">{CONVERSATION_STATUS_LABELS[row.status] ?? row.status}</span>
                  {row.waitingReply ? <span className="chip chip-warning" style={{ marginLeft: 4 }}>esperando</span> : null}
                </td>
                <td className="cell-third" data-label="Cliente">
                  {row.lead ? (
                    <span className={`chip${row.lead.status === 'WON' ? ' chip-positive' : row.lead.status === 'LOST' ? ' chip-negative' : ''}`}>
                      {STATUS_LABELS[row.lead.status]}
                    </span>
                  ) : <span className="muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
